/**
 * Utility for managing Terms of Service acceptance status in localStorage
 */

const TERMS_ACCEPTED_KEY = 'kiloba_terms_accepted';
const TERMS_VERSION_KEY = 'kiloba_terms_version';

// Version of terms - increment this when terms are updated
const CURRENT_TERMS_VERSION = '1.0.0';

export class TermsManager {
  /**
   * Check if user has accepted the current version of terms
   */
  static hasAcceptedTerms(): boolean {
    try {
      const isAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY) === 'true';
      const acceptedVersion = localStorage.getItem(TERMS_VERSION_KEY);
      
      // User must have accepted terms AND it must be the current version
      return isAccepted && acceptedVersion === CURRENT_TERMS_VERSION;
    } catch (error) {
      console.error('Error checking terms acceptance:', error);
      return false;
    }
  }

  /**
   * Mark terms as accepted for the current version
   */
  static acceptTerms(): void {
    try {
      localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
      localStorage.setItem(TERMS_VERSION_KEY, CURRENT_TERMS_VERSION);
      
      // Also save timestamp for audit purposes
      localStorage.setItem('kiloba_terms_accepted_at', new Date().toISOString());
      
      console.log('Terms of service accepted and saved to localStorage');
    } catch (error) {
      console.error('Error saving terms acceptance:', error);
    }
  }

  /**
   * Clear terms acceptance (for testing or when terms are updated)
   */
  static clearTermsAcceptance(): void {
    try {
      localStorage.removeItem(TERMS_ACCEPTED_KEY);
      localStorage.removeItem(TERMS_VERSION_KEY);
      localStorage.removeItem('kiloba_terms_accepted_at');
      
      console.log('Terms acceptance cleared from localStorage');
    } catch (error) {
      console.error('Error clearing terms acceptance:', error);
    }
  }

  /**
   * Get acceptance details for debugging
   */
  static getAcceptanceDetails(): {
    isAccepted: boolean;
    version: string | null;
    acceptedAt: string | null;
  } {
    try {
      return {
        isAccepted: localStorage.getItem(TERMS_ACCEPTED_KEY) === 'true',
        version: localStorage.getItem(TERMS_VERSION_KEY),
        acceptedAt: localStorage.getItem('kiloba_terms_accepted_at')
      };
    } catch (error) {
      console.error('Error getting acceptance details:', error);
      return {
        isAccepted: false,
        version: null,
        acceptedAt: null
      };
    }
  }

  /**
   * Check if user needs to accept terms (for app login)
   */
  static needsToAcceptTerms(): boolean {
    return !this.hasAcceptedTerms();
  }
}

export default TermsManager;